import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PageContainer } from '../../../components/ui/PageContainer';
import { QuestionnaireForm } from '../../../components/review360/QuestionnaireForm';
import {
  useReview360Cycle,
  useReview360ResponseDetail,
  useSaveResponseDraft,
  useSubmitResponse,
} from '../../../hooks/useApi';
import { useAnalytics, AnalyticsEvents } from '../../../analytics';
import { showToast } from '../../../utils/toast';
import { Review360Answer, QuestionSection } from '../../../types';

interface AnswerEntry {
  sectionId: string;
  value: number | string;
}

const RESPONSE_STATUS_COLORS: Record<string, string> = {
  draft: 'yellow',
  submitted: 'teal',
};

export const Review360Respond: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cycleId, responseId } = useParams<{ cycleId: string; responseId: string }>();
  const { track } = useAnalytics();

  const { data: cycle, isLoading: isCycleLoading } = useReview360Cycle(cycleId ?? '');
  const { data: response, isLoading: isResponseLoading } = useReview360ResponseDetail(
    cycleId ?? '',
    responseId ?? '',
  );

  const saveDraft = useSaveResponseDraft();
  const submitResponse = useSubmitResponse();

  const [answersMap, setAnswersMap] = useState<Map<string, AnswerEntry>>(new Map());
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDraftSaved, setIsDraftSaved] = useState(false);

  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftIndicatorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize answers from response data
  useEffect(() => {
    if (response?.answers && !isInitialized) {
      const map = new Map<string, AnswerEntry>();
      response.answers.forEach((a) => {
        map.set(a.questionId, { sectionId: a.sectionId, value: a.value });
      });
      setAnswersMap(map);
      setIsInitialized(true);
    }
  }, [response, isInitialized]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (draftIndicatorTimerRef.current) clearTimeout(draftIndicatorTimerRef.current);
    };
  }, []);

  const mapToAnswersArray = useCallback((map: Map<string, AnswerEntry>): Review360Answer[] => {
    return Array.from(map.entries()).map(([questionId, entry]) => ({
      questionId,
      sectionId: entry.sectionId,
      value: entry.value,
    }));
  }, []);

  const doSave = useCallback(
    (map: Map<string, AnswerEntry>) => {
      if (!cycleId || !responseId) return;
      saveDraft.mutate(
        { cycleId, responseId, answers: mapToAnswersArray(map) },
        {
          onSuccess: () => {
            setIsDraftSaved(true);
            if (draftIndicatorTimerRef.current) clearTimeout(draftIndicatorTimerRef.current);
            draftIndicatorTimerRef.current = setTimeout(() => setIsDraftSaved(false), 2000);
          },
        },
      );
    },
    [cycleId, responseId, saveDraft, mapToAnswersArray],
  );

  const handleAnswerChange = useCallback(
    (questionId: string, sectionId: string, value: number | string) => {
      setAnswersMap((prev) => {
        const next = new Map(prev);
        next.set(questionId, { sectionId, value });

        // Debounced auto-save
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(() => doSave(next), 2000);

        return next;
      });
    },
    [doSave],
  );

  const handleSave = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    doSave(answersMap);
  }, [answersMap, doSave]);

  const handleSubmit = useCallback(() => {
    if (!cycleId || !responseId) return;

    const sections: QuestionSection[] = cycle?.questionTemplate ?? [];
    const raterRole = response?.raterRole ?? '';

    // Validate required questions
    const isRequiredMissing = sections.some((section) =>
      section.questions.some((q) => {
        const isApplicable =
          !q.applicableRoles ||
          q.applicableRoles.length === 0 ||
          q.applicableRoles.includes(raterRole as never);
        if (!isApplicable || !q.required) return false;
        const answer = answersMap.get(q._id);
        return !answer || answer.value === '' || answer.value == null;
      }),
    );

    if (isRequiredMissing) {
      showToast({
        title: t('review360.requiredQuestion'),
        message: t('review360.fillRequired'),
        color: 'red',
      });
      return;
    }

    modals.openConfirmModal({
      title: t('review360.submitResponse'),
      centered: true,
      children: <Text size="sm">{t('review360.submitConfirm')}</Text>,
      labels: { confirm: t('common.confirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'teal' },
      onConfirm: async () => {
        // Save current answers first, then submit
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

        try {
          await saveDraft.mutateAsync({
            cycleId,
            responseId,
            answers: mapToAnswersArray(answersMap),
          });
          await submitResponse.mutateAsync({ cycleId, responseId });

          track(AnalyticsEvents.REVIEW_360_RESPONSE_SUBMITTED, {
            cycleId,
            responseId,
            raterRole: response?.raterRole,
          });

          showToast({
            title: t('review360.responseSubmitted'),
            message: t('review360.responseSubmittedMsg'),
            color: 'teal',
          });

          navigate('/review-360');
        } catch {
          showToast({
            title: t('common.error'),
            message: t('common.unknownError'),
            color: 'red',
          });
        }
      },
    });
  }, [
    cycleId,
    responseId,
    cycle,
    response,
    answersMap,
    saveDraft,
    submitResponse,
    mapToAnswersArray,
    track,
    navigate,
    t,
  ]);

  const isLoading = isCycleLoading || isResponseLoading;
  const isSubmitted = response?.status === 'submitted';

  if (isLoading) {
    return (
      <PageContainer>
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </PageContainer>
    );
  }

  const subjectName = typeof response?.subjectId === 'object' ? response.subjectId.name : '';
  const sections: QuestionSection[] = cycle?.questionTemplate ?? [];

  return (
    <PageContainer>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/review-360')}
            >
              {t('common.back')}
            </Button>
            <Title order={3}>
              {t('review360.respond')}: {subjectName}
            </Title>
          </Group>

          <Group gap="xs">
            {response?.raterRole && (
              <Badge variant="light">{t(`review360.roles.${response.raterRole}`)}</Badge>
            )}
            <Badge color={RESPONSE_STATUS_COLORS[response?.status ?? 'draft'] ?? 'gray'}>
              {t(`review360.phases.${response?.status === 'submitted' ? 'completed' : 'draft'}`)}
            </Badge>
            {isDraftSaved && (
              <Badge color="teal" variant="light" leftSection={<IconCheck size={12} />}>
                {t('review360.draftSaved')}
              </Badge>
            )}
          </Group>
        </Group>

        {/* Form */}
        <QuestionnaireForm
          sections={sections}
          answers={mapToAnswersArray(answersMap)}
          raterRole={response?.raterRole ?? ''}
          isSubmitted={isSubmitted}
          isClosed={!!cycle && cycle.status !== 'in_progress'}
          onAnswerChange={handleAnswerChange}
          onSave={handleSave}
          onSubmit={handleSubmit}
          isSaving={saveDraft.isPending}
          isSubmitting={submitResponse.isPending}
        />
      </Stack>
    </PageContainer>
  );
};
